"use client";

import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import BrandShop from "../components/brand/BrandShop";
import ProductGrid from "../components/ProductGrid";
import Pager from "./Pager";
import { paginate } from "./listing";
import { DEFAULT_STATE, parseState, sortProducts, type BrandShopState, type LineChip, type TypeChip } from "../../lib/brand-shop";
import type { CatalogLite } from "../../lib/types";

export interface BrandListingProps {
  brand: { slug: string; label: string };
  path: string;
  siteUrl: string;
  products: CatalogLite[];
  types: TypeChip[];
  lines: LineChip[];
  brandAskUrl: string;
  /** Very large brands skip BrandShop's client filtering and use the plain paged grid. */
  paginated: boolean;
}

function Shop({
  brand,
  path,
  siteUrl,
  products,
  types,
  lines,
  brandAskUrl,
  initial,
}: Omit<BrandListingProps, "paginated"> & { initial: BrandShopState }) {
  return (
    <BrandShop
      brand={brand}
      path={path}
      siteUrl={siteUrl}
      products={products}
      types={types}
      lines={lines}
      initial={initial}
      brandAskUrl={brandAskUrl}
    />
  );
}

function Paged({ path, products, count, page }: { path: string; products: CatalogLite[]; count: number; page?: string | null }) {
  const { items, current, totalPages } = paginate(sortProducts(products, "featured"), page);
  return (
    <>
      <p className="eyebrow mb-8 text-ink/60">{count.toLocaleString()} pieces</p>
      <ProductGrid products={items} />
      <Pager current={current} totalPages={totalPages} hrefFor={(n) => `${path}?page=${n}`} />
    </>
  );
}

function Live(props: BrandListingProps) {
  const sp = useSearchParams();
  // Read the URL once, on mount. BrandShop keeps the URL in step with its own
  // state via replaceState, which also updates useSearchParams; re-deriving
  // `initial` from it would just remount the shop on every filter click.
  const [initial] = useState(() =>
    parseState(
      {
        type: sp.get("type") ?? undefined,
        line: sp.get("line") ?? undefined,
        stock: sp.get("stock") ?? undefined,
        sort: sp.get("sort") ?? undefined,
      },
      props.types,
      props.lines,
    ),
  );
  if (props.paginated) return <Paged path={props.path} products={props.products} count={props.products.length} page={sp.get("page")} />;
  return <Shop {...props} initial={initial} />;
}

/**
 * A brand page's shop. The route reads no request data (so it is cached, ISR);
 * the URL's ?type= ?line= ?stock= ?sort= (and ?page= for very large brands) are
 * applied here on the client. The Suspense fallback is the unfiltered shop,
 * which is the complete static HTML crawlers see.
 */
export default function BrandListing(props: BrandListingProps) {
  return (
    <Suspense
      fallback={
        props.paginated ? (
          <Paged path={props.path} products={props.products} count={props.products.length} />
        ) : (
          <Shop {...props} initial={DEFAULT_STATE} />
        )
      }
    >
      <Live {...props} />
    </Suspense>
  );
}
